import { ApiError } from '../../services/BaseApiService'
import type { ESignFieldKind, ESignRequestStatus, ESignSignerStatus } from '../../services/esignApiService'
import type { RenderJob } from '../../types'
import { RenderJobStatus } from '../../constants'
import { feedback } from '../../styles/theme'

/** Default field sizes in PDF points. */
export const FIELD_SIZES: Record<ESignFieldKind, { width: number; height: number }> = {
  signature: { width: 180, height: 40 },
  initials:  { width: 60,  height: 30 },
  date:      { width: 100, height: 20 },
  name:      { width: 160, height: 20 },
}

export const FIELD_LABELS: Record<ESignFieldKind, string> = {
  signature: 'Signature',
  initials:  'Initials',
  date:      'Date',
  name:      'Name',
}

export const SIGNER_COLORS = [
  '#0F9D92', '#2563EB', '#D97706', '#9333EA', '#DC2626',
  '#0891B2', '#65A30D', '#DB2777', '#4F46E5', '#B45309',
]

export const signerColor = (i: number) => SIGNER_COLORS[i % SIGNER_COLORS.length]!

const NEUTRAL = { bg: '#EEF0F1', text: '#6B7180', border: '#EEF0F1' }

export const REQUEST_STATUS_CHIP: Record<ESignRequestStatus, { bg: string; text: string; border: string }> = {
  pending:   feedback.info,
  completed: feedback.success,
  declined:  feedback.danger,
  voided:    NEUTRAL,
  expired:   feedback.warning,
}

export const SIGNER_STATUS_CHIP: Record<ESignSignerStatus, { bg: string; text: string; border: string }> = {
  waiting:  NEUTRAL,
  pending:  feedback.info,
  signed:   feedback.success,
  declined: feedback.danger,
}

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export function fmtDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return withTime ? d.toLocaleString() : d.toLocaleDateString()
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export const PLAN_UPGRADE_REQUIRED = 'PLAN_UPGRADE_REQUIRED'

export const isPlanUpgradeError = (err: unknown) =>
  err instanceof ApiError && err.status === 403 && err.code === PLAN_UPGRADE_REQUIRED

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message || fallback : fallback
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const isValidEmail = (s: string) => EMAIL_RE.test(s.trim())

/** A completed PDF render whose output is still stored (not no-store, not purged). */
export function isViewablePdfJob(job: RenderJob): boolean {
  if (job.renderStatusLookupId !== RenderJobStatus.COMPLETE) return false
  if (!job.outputBlobPath || job.purgedAt) return false
  if (job.outputFormatLookupId != null && job.outputFormatLookupId !== OUTPUT_FORMAT_PDF) return false
  const fmt = renderOutputFormat(job.renderOptionsJson)
  return fmt == null || fmt === 'pdf'
}

/** The render was made with a PDF password: the job APIs report HasUserPassword / HasOwnerPassword in its options. */
export function isPasswordProtectedJob(job: RenderJob): boolean {
  const pdf = renderOptions(job.renderOptionsJson)?.PdfOptions ?? renderOptions(job.renderOptionsJson)?.pdfOptions
  if (!pdf || typeof pdf !== 'object') return false
  const o = pdf as Record<string, unknown>
  return o.HasUserPassword === true || o.HasOwnerPassword === true || o.hasUserPassword === true || o.hasOwnerPassword === true
}

/** A PDF that can be sent for signature: not password-protected, which the server refuses (signers couldn't open it). */
export function isSignableRenderJob(job: RenderJob): boolean {
  return isViewablePdfJob(job) && !isPasswordProtectedJob(job)
}

const OUTPUT_FORMAT_PDF = 10

function renderOptions(json: string | null | undefined): Record<string, unknown> | null {
  if (!json) return null
  try {
    const o = JSON.parse(json) as unknown
    return o && typeof o === 'object' ? o as Record<string, unknown> : null
  } catch {
    return null
  }
}

function renderOutputFormat(json: string | null | undefined): string | null {
  const o = renderOptions(json)
  const f = o?.OutputFormat ?? o?.outputFormat
  return typeof f === 'string' && f ? f.toLowerCase() : null
}
